import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { DashboardIcon, ManagementIcon, ReportsIcon, CatalogIcon, LogoutIcon } from '../ui/Icons';
import { Star } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
}

const navItems: NavItem[] = [
  { to: '/admin', label: 'Dashboard', Icon: DashboardIcon as LucideIcon },
  { to: '/admin/orders', label: 'Orders', Icon: ReportsIcon as LucideIcon },
  { to: '/admin/resellers', label: 'Resellers', Icon: ManagementIcon as LucideIcon },
  { to: '/admin/products', label: 'Product Catalog', Icon: CatalogIcon as LucideIcon },
  { to: '/admin/feedback', label: 'Feedback', Icon: Star as LucideIcon },
];

export const AdminSidebar: React.FC = () => {
  const { user, logout } = useAuth();

  const initials = user?.name
    ? user.name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()
    : 'A';

  return (
    <aside style={{
      width: '232px',
      height: '100vh',
      background: 'var(--sidebar-bg)',
      borderRight: '1px solid var(--sidebar-border)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Logo */}
      <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <img src="/salam-logo-light.svg" alt="Salam" style={{ height: '30px', display: 'block' }} />
        <div style={{ fontSize: '11px', color: 'var(--p)', marginTop: '3px', fontWeight: 600 }}>
          Admin Portal
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/admin'}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '9px 12px',
                borderRadius: '8px',
                fontSize: '13.5px',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'var(--p)' : 'var(--text-2)',
                background: isActive ? 'var(--p-light)' : 'transparent',
                cursor: 'pointer',
                transition: 'all .15s',
              }}>
                <item.Icon size={16} color={isActive ? 'var(--p)' : 'var(--text-2)'} />
                {item.label}
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid var(--border)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <div style={{
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          background: 'var(--p-light)',
          color: 'var(--p)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '13px',
          flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.name ?? '—'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--p)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Salam Admin
          </div>
        </div>
        <button
          onClick={logout}
          title="Sign Out"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex', padding: '4px', borderRadius: '6px', flexShrink: 0 }}
        >
          <LogoutIcon size={16} />
        </button>
      </div>
    </aside>
  );
};
